import * as R from 'ramda';
import { CSS } from '@dnd-kit/utilities';
import { shouldUpdate } from 'react-recompose';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import React, { useMemo, useState, useCallback } from 'react';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// icons
import * as I from '../../../../svgs';
// hocs
import { withDnDStartEnd } from '../../../../hocs';
// ui
import { Box, Span, Text, Flex } from '../../../../ui';
// component edit-report
import { FieldSelect } from '../field-select';
// feature edit-report
import { setOptions, getDnDReorderResult, getSortFieldSortableId } from '../../helpers';
//////////////////////////////////////////////////

const dndModifiers = [restrictToVerticalAxis, restrictToParentElement];

const SortHeader = (props) => {
  const { reportSortList, handleAddSortField } = props;

  const sortLength = R.length(reportSortList);

  return (
    <Flex
      p='0 15px'
      height={40}
      borderBottom='1px solid'
      justifyContent='space-between'
      color={G.getTheme('colors.light.black')}
      bg={G.getTheme('colors.light.lightGrey')}
      borderColor={G.getTheme('colors.lightGrey')}
    >
      <Span>{G.getWindowLocale('titles:sort', 'Sort')}</Span>
      {
        R.lt(sortLength, 5) &&
        <Box
          ml={10}
          fontSize={12}
          cursor='pointer'
          onClick={handleAddSortField}
          color={G.getTheme('colors.light.blue')}
        >
          {G.getWindowLocale('titles:add-field', 'Add Field')}
        </Box>
      }
    </Flex>
  );
};

const FilterItemAction = (props) => {
  const { fieldIndex, handleRemoveSortField } = props;

  return (
    <Flex>
      <Box>
        <Flex
          ml={10}
          alignItems='center'
          title={G.getWindowLocale('titles:drag-and-drop', 'Drag And Drop')}
        >
          {I.dnd(G.getTheme('colors.dark.blue'), 20, 20)}
        </Flex>
      </Box>
      <Box>
        <Flex
          ml={10}
          cursor='pointer'
          alignItems='center'
          onPointerDown={G.stopPropagation}
          onClick={() => handleRemoveSortField(fieldIndex)}
          title={G.getWindowLocale('titles:remove', 'Remove')}
        >
          {I.trash(G.getTheme('colors.dark.blue'))}
        </Flex>
      </Box>
    </Flex>
  );
};

const SortItem = (props) => {
  const {
    name,
    order,
    zIndex,
    fieldIndex,
    handleSelectSortName,
    handleChangeFieldOrder,
    availableForSortOptions,
    availableFieldOptionsByValue,
  } = props;

  const orderIcon = G.ifElse(R.equals(order, GC.SORT_TYPE_ASC), I.arrowUp, I.arrowDown);
  const orderToUpdate = G.ifElse(R.equals(order, GC.SORT_TYPE_ASC), GC.SORT_TYPE_DESC, GC.SORT_TYPE_ASC);
  const field = R.prop(name, availableFieldOptionsByValue);

  const options = useMemo(() => (
    G.ifElse(field, R.concat([field], availableForSortOptions), availableForSortOptions)
  ), [field, availableForSortOptions]);

  return (
    <Flex py={10} alignItems='center' zIndex={zIndex}>
      <FilterItemAction {...props} />
      <FieldSelect
        value={name}
        options={options}
        selectedOption={field}
        fieldIndex={fieldIndex}
        action={handleSelectSortName}
        id={`report-sort-${fieldIndex}`}
      />
      <Flex pr={10} onPointerDown={G.stopPropagation}>
        <Span
          title={order}
          cursor='pointer'
          onClick={() => handleChangeFieldOrder({ fieldIndex, order: orderToUpdate })}
        >
          {orderIcon(G.getTheme('colors.dark.blue'))}
        </Span>
      </Flex>
    </Flex>
  );
};

const SortRowContent = (props) => {
  const {
    field,
    index,
    maxIndex,
    isDragging,
    itemBorderColor,
    handleSelectSortName,
    handleRemoveSortField,
    handleChangeFieldOrder,
    availableForSortOptions,
    availableFieldOptionsByValue,
  } = props;

  return (
    <Box
      width='100%'
      alignItems='start'
      border='1px solid'
      borderColor={itemBorderColor}
      opacity={G.ifElse(isDragging, 0.95, 1)}
      zIndex={R.subtract(maxIndex, R.inc(index))}
    >
      <SortItem
        {...field}
        fieldIndex={index}
        handleSelectSortName={handleSelectSortName}
        handleRemoveSortField={handleRemoveSortField}
        handleChangeFieldOrder={handleChangeFieldOrder}
        availableForSortOptions={availableForSortOptions}
        availableFieldOptionsByValue={availableFieldOptionsByValue}
      />
    </Box>
  );
};

const SortableSortItem = (props) => {
  const { field, index, itemData } = props;

  const sortableId = getSortFieldSortableId(field, index);

  const {
    listeners,
    transform,
    attributes,
    setNodeRef,
    transition,
  } = useSortable({
    id: sortableId,
    animateLayoutChanges: () => false,
  });

  const isHidden = R.equals(sortableId, itemData.draggingFieldId);

  const sortableStyle = {
    transition,
    boxSizing: 'border-box',
    opacity: isHidden ? 0.1 : 1,
    cursor: isHidden ? 'grabbing' : 'grab',
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      {...attributes}
      {...listeners}
    >
      <SortRowContent
        {...itemData}
        field={field}
        index={index}
        isDragging={false}
      />
    </div>
  );
};

const enhance = shouldUpdate((props, nextProps) => {
  const toDiff = [
    'dndProcessing',
    'draggingFieldId',
    'reportSortList',
    'availableFields',
    'availableForSort',
  ];

  const shouldUpdate = R.any((field) => (
    G.notEquals(R.prop(field, props), R.prop(field, nextProps))
  ))(toDiff);

  return shouldUpdate;
});

const Fields = enhance((props) => {
  const {
    dndProcessing,
    reportSortList,
    draggingFieldId,
    availableFields,
    availableForSort,
    handleSelectSortName,
    handleRemoveSortField,
    handleChangeFieldOrder,
  } = props;

  const border = G.ifElse(G.isTrue(dndProcessing), '1px dashed grey', 'none');

  const itemBorderColor = G.getTheme('colors.lightGrey');
  const maxIndex = R.multiply(R.length(availableFields), 5);

  const availableForSortOptions = useMemo(
    () => R.or(setOptions(availableForSort), []),
    [availableForSort],
  );

  const availableFieldOptionsByValue = useMemo(
    () => R.indexBy(R.prop(GC.FIELD_VALUE), R.or(setOptions(availableFields), [])),
    [availableFields],
  );

  const sortableIds = useMemo(
    () => R.addIndex(R.map)(
      (field, index) => getSortFieldSortableId(field, index),
      reportSortList,
    ),
    [reportSortList],
  );

  const draggingIndex = R.indexOf(draggingFieldId, sortableIds);

  const draggingField = R.gte(draggingIndex, 0)
    ? R.path([draggingIndex], reportSortList)
    : null;

  const itemData = useMemo(() => ({
    maxIndex,
    draggingFieldId,
    itemBorderColor,
    handleSelectSortName,
    handleRemoveSortField,
    handleChangeFieldOrder,
    availableForSortOptions,
    availableFieldOptionsByValue,
  }), [
    maxIndex,
    draggingFieldId,
    itemBorderColor,
    handleSelectSortName,
    handleRemoveSortField,
    handleChangeFieldOrder,
    availableForSortOptions,
    availableFieldOptionsByValue,
  ]);

  return (
    <Flex border={border} flexDirection='column'>
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        {
          reportSortList.map((field, index) => (
            <SortableSortItem
              field={field}
              index={index}
              itemData={itemData}
              key={getSortFieldSortableId(field, index)}
            />
          ))
        }
      </SortableContext>
      <DragOverlay>
        {
          draggingField ? (
            <SortRowContent
              isDragging={true}
              maxIndex={maxIndex}
              field={draggingField}
              index={draggingIndex}
              itemBorderColor={itemBorderColor}
              handleSelectSortName={handleSelectSortName}
              handleRemoveSortField={handleRemoveSortField}
              handleChangeFieldOrder={handleChangeFieldOrder}
              availableForSortOptions={availableForSortOptions}
              availableFieldOptionsByValue={availableFieldOptionsByValue}
            />
          ) : null
        }
      </DragOverlay>
    </Flex>
  );
});

const onDragEnd = (result, props) => {
  const { handleDragEnd, handleSortDragEnd } = props;
  handleSortDragEnd(result);
  handleDragEnd(result);
};

const SortList = withDnDStartEnd((props) => {
  const { reportSortList, handleDragEnd, handleDragStart } = props;

  const [draggingFieldId, setDraggingFieldId] = useState(null);

  const sortLength = R.keys(reportSortList);

  const handleSortListDragStart = useCallback((event) => {
    setDraggingFieldId(R.path(['active', 'id'], event));

    handleDragStart(event);
  }, [handleDragStart]);

  const handleSortListDragEnd = useCallback((event) => {
    setDraggingFieldId(null);

    const sortableIds = G.mapIndexed(
      (field, index) => getSortFieldSortableId(field, index),
      reportSortList,
    );

    const result = R.defaultTo({}, getDnDReorderResult(event, sortableIds));

    onDragEnd(result, props);
  }, [props, reportSortList]);

  const handleSortListDragCancel = useCallback(() => {
    setDraggingFieldId(null);

    handleDragEnd();
  }, [handleDragEnd]);

  return (
    <DndContext
      modifiers={dndModifiers}
      id='edit-report-sort-list'
      onDragEnd={handleSortListDragEnd}
      onDragStart={handleSortListDragStart}
      onDragCancel={handleSortListDragCancel}
    >
      <Box width='auto'>
        <SortHeader {...props} />
        <Box>
          {
            G.isNilOrEmpty(sortLength) &&
            <Box p={15}>
              <Flex flexDirection='column'>
                <Text fontSize={16} color={G.getTheme('colors.dark.grey')}>
                  {
                    G.getWindowLocale(
                      'messages:you-do-not-have-any-sorting-for-this-report',
                      "You don't have any sorting for this Report",
                    )
                  }
                </Text>
              </Flex>
            </Box>
          }
        </Box>
        {
          G.isNotNilAndNotEmpty(sortLength) &&
          <Fields {...props} draggingFieldId={draggingFieldId} />
        }
      </Box>
    </DndContext>
  );
});

export default SortList;
